// Sensor Debug Tool - Allows superadmins to enable raw payload logging for specific sensors
// This is used to debug sensors that aren't being decoded correctly

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import Icon from '../../core/Icon';
import {
  Content,
  Header,
  Title,
  Panel,
  PanelTitle,
  Button,
  Input,
  Label,
  InfoBoxContainer,
  WarningBoxContainer,
} from './styles';

// GraphQL Queries and Mutations
const SENSOR_DEBUG_LIST_QUERY = gql`
  query SensorDebugList {
    sensorDebugList {
      mac
      expiresAt
    }
  }
`;

const ENABLE_SENSOR_DEBUG_MUTATION = gql`
  mutation EnableSensorDebug($mac: String!) {
    enableSensorDebug(mac: $mac) {
      mac
      expiresAt
    }
  }
`;

const DISABLE_SENSOR_DEBUG_MUTATION = gql`
  mutation DisableSensorDebug($mac: String!) {
    disableSensorDebug(mac: $mac)
  }
`;

// Blocked sensors queries and mutations
const SENSORS_BLOCKED_QUERY = gql`
  query SensorsBlocked {
    sensorsBlocked
  }
`;

const UNBLOCK_SENSOR_MUTATION = gql`
  mutation UnblockSensor($mac: String!) {
    unblockSensor(mac: $mac)
  }
`;

const UNBLOCK_SENSORS_MUTATION = gql`
  mutation UnblockSensors($ids: [String!]!) {
    unblockSensors(ids: $ids)
  }
`;

interface SensorDebugEntry {
  mac: string;
  expiresAt: string;
}

interface SensorDebugListData {
  sensorDebugList: SensorDebugEntry[];
}

interface SensorsBlockedData {
  sensorsBlocked: string[];
}

export const SensorDebug: React.FC = () => {
  const [macInput, setMacInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Query for current debug list
  const { data, loading, refetch } = useQuery<SensorDebugListData>(SENSOR_DEBUG_LIST_QUERY, {
    pollInterval: 30000, // Refresh every 30 seconds to update expiration times
  });

  // Mutations
  const [enableDebug, { loading: enabling }] = useMutation(ENABLE_SENSOR_DEBUG_MUTATION);
  const [disableDebug, { loading: disabling }] = useMutation(DISABLE_SENSOR_DEBUG_MUTATION);

  // Blocked sensors query
  const {
    data: blockedData,
    loading: blockedLoading,
    refetch: refetchBlocked,
  } = useQuery<SensorsBlockedData>(SENSORS_BLOCKED_QUERY, {
    pollInterval: 60000, // Refresh every minute
  });

  // Blocked sensors mutations
  const [unblockSensor, { loading: unblocking }] = useMutation(UNBLOCK_SENSOR_MUTATION);
  const [unblockSensors, { loading: unblockingAll }] = useMutation(UNBLOCK_SENSORS_MUTATION);

  // Handle enable debug
  const handleEnableDebug = useCallback(async () => {
    if (!macInput.trim()) {
      setError('Please enter a MAC address');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const result = await enableDebug({
        variables: { mac: macInput.trim() },
      });
      const normalizedMac = result.data?.enableSensorDebug?.mac;
      setSuccess(`Debug enabled for ${normalizedMac}. Raw payloads will be logged at INFO level for 5 minutes.`);
      setMacInput('');
      refetch();
    } catch (err) {
      setError(`Failed to enable debug: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [macInput, enableDebug, refetch]);

  // Handle disable debug
  const handleDisableDebug = useCallback(
    async (mac: string) => {
      setError(null);
      setSuccess(null);

      try {
        await disableDebug({
          variables: { mac },
        });
        setSuccess(`Debug disabled for ${mac}`);
        refetch();
      } catch (err) {
        setError(`Failed to disable debug: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    },
    [disableDebug, refetch]
  );

  // Handle unblock single sensor
  const handleUnblockSensor = useCallback(
    async (mac: string) => {
      setError(null);
      setSuccess(null);

      try {
        const result = await unblockSensor({
          variables: { mac },
        });
        if (result.data?.unblockSensor) {
          setSuccess(`Sensor ${mac} has been unblocked and can now be processed again.`);
        } else {
          setSuccess(`Sensor ${mac} was not in the blocked list.`);
        }
        refetchBlocked();
      } catch (err) {
        setError(`Failed to unblock sensor: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    },
    [unblockSensor, refetchBlocked]
  );

  // Handle unblock all sensors
  const handleUnblockAll = useCallback(async () => {
    setError(null);
    setSuccess(null);

    const blockedList = blockedData?.sensorsBlocked || [];
    if (blockedList.length === 0) {
      setError('No blocked sensors to unblock');
      return;
    }

    try {
      const result = await unblockSensors({
        variables: { ids: blockedList },
      });
      const unblocked = result.data?.unblockSensors || [];
      setSuccess(`Unblocked ${unblocked.length} sensor(s). They can now be processed again.`);
      refetchBlocked();
    } catch (err) {
      setError(`Failed to unblock sensors: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [blockedData, unblockSensors, refetchBlocked]);

  // Format remaining time
  const formatTimeRemaining = (expiresAt: string): string => {
    const now = Date.now();
    const expires = new Date(expiresAt).getTime();
    const remaining = expires - now;

    if (remaining <= 0) return 'Expired';

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    }
    return `${seconds}s remaining`;
  };

  const debugList = data?.sensorDebugList || [];
  const blockedList = blockedData?.sensorsBlocked || [];

  return (
    <Content>
      <Header>
        <Title>
          <Icon className="material-icons">bug_report</Icon>
          Sensor Debug Tool
        </Title>
      </Header>

      <InfoBoxContainer>
        <strong>Instructions:</strong>
        <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
          <li>Enter a sensor MAC address to enable debug logging (e.g., C30000526C28)</li>
          <li>The sensor&apos;s raw payload will be logged at INFO level for 5 minutes (auto-expires)</li>
          <li>
            Check CloudWatch logs for <code>event: sensor_debug_payload</code> entries
          </li>
          <li>
            Filter: <code>filter @message like /sensor_debug_payload/</code>
          </li>
        </ol>
      </InfoBoxContainer>

      <WarningBoxContainer>
        <Icon className="material-icons" style={{ color: '#ff9800' }}>
          warning
        </Icon>
        <span>
          <strong>Note:</strong> Debug logging adds INFO-level logs for each signal from the sensor. Use sparingly in
          production.
        </span>
      </WarningBoxContainer>

      {error && (
        <InfoBoxContainer variant="error">
          <Icon className="material-icons" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}>
            error
          </Icon>
          {error}
        </InfoBoxContainer>
      )}

      {success && (
        <InfoBoxContainer variant="success">
          <Icon className="material-icons" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}>
            check_circle
          </Icon>
          {success}
        </InfoBoxContainer>
      )}

      <Panel style={{ marginBottom: '1.5rem' }}>
        <PanelTitle>Enable Debug Logging</PanelTitle>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Label>
            Sensor MAC:
            <Input
              type="text"
              value={macInput}
              onChange={e => setMacInput(e.target.value)}
              placeholder="C30000526C28"
              style={{ width: '180px', textTransform: 'uppercase' }}
              onKeyDown={e => e.key === 'Enter' && handleEnableDebug()}
            />
          </Label>
          <Button primary onClick={handleEnableDebug} disabled={enabling || !macInput.trim()}>
            <Icon className="material-icons">play_arrow</Icon>
            {enabling ? 'Enabling...' : 'Enable Debug (5 min)'}
          </Button>
        </div>
        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', opacity: 0.7 }}>
          MAC format: 12 hex characters (e.g., C30000526C28 or C3:00:00:52:6C:28)
        </div>
      </Panel>

      <Panel>
        <PanelTitle>
          Active Debug Sessions ({debugList.length})
          <Button
            onClick={() => refetch()}
            style={{ marginLeft: '1rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
          >
            <Icon className="material-icons" style={{ fontSize: '1rem' }}>
              refresh
            </Icon>
            Refresh
          </Button>
        </PanelTitle>

        {loading ? (
          <div>Loading...</div>
        ) : debugList.length === 0 ? (
          <div style={{ opacity: 0.6, fontStyle: 'italic' }}>No active debug sessions</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {debugList.map(entry => (
              <div
                key={entry.mac}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                  borderLeft: '3px solid #4CAF50',
                }}
              >
                <div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{entry.mac}</div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.25rem' }}>
                    {formatTimeRemaining(entry.expiresAt)}
                  </div>
                </div>
                <Button danger onClick={() => handleDisableDebug(entry.mac)} disabled={disabling}>
                  <Icon className="material-icons">stop</Icon>
                  Disable
                </Button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel style={{ marginTop: '1.5rem' }}>
        <PanelTitle>
          <Icon className="material-icons" style={{ marginRight: '0.5rem', color: '#f44336' }}>
            block
          </Icon>
          Blocked Sensors ({blockedList.length})
          <Button
            onClick={() => refetchBlocked()}
            style={{ marginLeft: '1rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
          >
            <Icon className="material-icons" style={{ fontSize: '1rem' }}>
              refresh
            </Icon>
            Refresh
          </Button>
          {blockedList.length > 0 && (
            <Button
              danger
              onClick={handleUnblockAll}
              disabled={unblockingAll}
              style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
            >
              <Icon className="material-icons" style={{ fontSize: '1rem' }}>
                lock_open
              </Icon>
              {unblockingAll ? 'Unblocking...' : 'Unblock All'}
            </Button>
          )}
        </PanelTitle>

        <InfoBoxContainer>
          <strong>What are blocked sensors?</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            Sensors get blocked when they are deleted or when a race condition occurs (new sensor sends signals before
            being written to the database). Once blocked, their signals are filtered out.
          </p>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            If a sensor is blocked but should be active, click <strong>Unblock</strong> to allow it to be processed
            again. The sensor will be re-registered automatically when it sends its next signal.
          </p>
        </InfoBoxContainer>

        {blockedLoading ? (
          <div>Loading blocked sensors...</div>
        ) : blockedList.length === 0 ? (
          <div style={{ opacity: 0.6, fontStyle: 'italic', padding: '1rem 0' }}>
            <Icon
              className="material-icons"
              style={{ verticalAlign: 'middle', marginRight: '0.5rem', color: '#4CAF50' }}
            >
              check_circle
            </Icon>
            No blocked sensors - all sensors can be processed normally
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {blockedList.map(mac => (
              <div
                key={mac}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(244, 67, 54, 0.1)',
                  borderRadius: '4px',
                  borderLeft: '3px solid #f44336',
                }}
              >
                <div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{mac}</div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.25rem' }}>
                    Signals from this sensor are being filtered out
                  </div>
                </div>
                <Button primary onClick={() => handleUnblockSensor(mac)} disabled={unblocking}>
                  <Icon className="material-icons">lock_open</Icon>
                  {unblocking ? 'Unblocking...' : 'Unblock'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel style={{ marginTop: '1.5rem' }}>
        <PanelTitle>CloudWatch Query Examples</PanelTitle>
        <div style={{ fontSize: '0.85rem' }}>
          <p>
            <strong>Find all debug payloads:</strong>
          </p>
          <pre
            style={{
              background: 'rgba(0,0,0,0.3)',
              padding: '0.5rem',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {`fields @timestamp, @message
| filter @message like /sensor_debug_payload/
| sort @timestamp desc
| limit 100`}
          </pre>

          <p style={{ marginTop: '1rem' }}>
            <strong>Find payloads for a specific MAC:</strong>
          </p>
          <pre
            style={{
              background: 'rgba(0,0,0,0.3)',
              padding: '0.5rem',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {`fields @timestamp, @message
| filter @message like /sensor_debug_payload/ and @message like /C30000526C28/
| sort @timestamp desc
| limit 100`}
          </pre>

          <p style={{ marginTop: '1rem' }}>
            <strong>Parse JSON for analysis:</strong>
          </p>
          <pre
            style={{
              background: 'rgba(0,0,0,0.3)',
              padding: '0.5rem',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {`fields @timestamp
| filter @message like /sensor_debug_payload/
| parse @message '"mac":"*"' as mac
| parse @message '"rawData":"*"' as rawData
| parse @message '"frameType":"*"' as frameType
| sort @timestamp desc
| limit 100`}
          </pre>
        </div>
      </Panel>
    </Content>
  );
};

export default SensorDebug;
